// view-truncate-ok-file: thanh tiêu đề chứng từ một dòng cao cố định (mã luôn hiện đủ, phần thừa của link danh mục / nhãn trạng thái cắt)
import React, { useMemo, useEffect, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useModuleTabs } from "./ModuleTabsContext";
import { ArrowLeft, MoreVertical, MessageSquare, Paperclip, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MobileOverflowMenu, useMobileOverflow, rankAction } from '@/components/shared/MobileOverflowMenu';
import { useDocHeaderStore } from "@/stores/useDocHeaderStore";
import { AdaptiveActionSplitter } from "@/components/shared/AdaptiveActionSplitter";

import EntityContextPanel from "@/components/shared/EntityContextPanel";
import UserDisplay from "@/components/shared/UserDisplay";

import type { BreadcrumbItem } from '@/shared/schemas';

interface StandardDetailLayoutProps {
    breadcrumbs?: BreadcrumbItem[];
    title: string | React.ReactNode;
    /** @deprecated Thanh tiêu đề không hiển thị subtitle (31/10/2026): dữ liệu xem ở thẻ thông tin, chỉ dẫn đặt trong thân trang */
    subtitle?: string | React.ReactNode;
    /** Tiêu đề chữ cho tab trình duyệt / lịch sử quay lại khi `title` là JSX (VD mã SP kèm huy hiệu phiên bản) */
    tabTitle?: string;
    status?: React.ReactNode;
    actions?: React.ReactNode;
    /** Workflow stepper rendered inline in header below title row */
    workflow?: React.ReactNode;
    children: React.ReactNode;
    onBack?: () => void;
    /** Force call custom onBack instead of smart app history navigation */
    overrideBack?: boolean;
    /** Explicitly hide back button (e.g. inside modal dialog or isZIndex) */
    hideBackButton?: boolean;
    /** True if opened inside ZIndexView modal/dialog */
    isZIndex?: boolean;
    showContextPanel?: boolean;
    entityId?: string;
    entityType?: string;
    showSync?: boolean;
    /** Timestamp footer: "Tạo: ... • Cập nhật: ..." */
    timestamps?: { created?: string | Date | null; updated?: string | Date | null; createdBy?: string | null; updatedBy?: string | null };
}

import { SyncStatusPill } from "@/components/shared/SyncStatusPill";
import { trpc, DATA_MUTATED_EVENT } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";

import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useNavigationHistoryStore, ENTITY_PAGELIST_MAP } from "@/stores/useNavigationHistory";

function fmtTs(d: string | Date | null | undefined): string {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' });
}

const StandardDetailLayout: React.FC<StandardDetailLayoutProps> = ({
    breadcrumbs,
    title,
    subtitle,
    tabTitle,
    status,
    actions,
    workflow,
    children,
    onBack,
    overrideBack = false,
    hideBackButton = false,
    isZIndex = false,
    showContextPanel = true,
    entityId,
    entityType = 'general',
    showSync = false,
    timestamps,
}) => {
    const navigate = useNavigate();
    const router = useRouter();
    const previousRoute = useNavigationHistoryStore((state) => state.getPreviousRoute());
    const updateCurrentTitle = useNavigationHistoryStore((state) => state.updateCurrentTitle);
    const setHasDocHeader = useDocHeaderStore((state) => state.setHasDocHeader);
    // Dưới thanh tab module: tiêu đề chứng từ đặt ngay dưới tab, không đưa lên thanh trên cùng
    const moduleTabs = useModuleTabs();
    const underModuleTabs = Boolean(moduleTabs) && !isZIndex;
    const inlineHeader = isZIndex || underModuleTabs;

    useEffect(() => {
        if (!underModuleTabs) return;
        moduleTabs!.setDocShell(true);
        return () => moduleTabs!.setDocShell(false);
    }, [underModuleTabs, moduleTabs]);

    // Thanh nút dưới đáy trên điện thoại: tối đa 4 nút mỗi hàng, phần còn lại vào "…"
    const mobileOverflow = useMobileOverflow(rankAction, 4);

    const [portalSlot, setPortalSlot] = useState<HTMLElement | null>(() => {
        if (typeof document !== 'undefined' && !inlineHeader) {
            return document.getElementById('apv-header-portal-slot');
        }
        return null;
    });

    useEffect(() => {
        if (inlineHeader) {
            setHasDocHeader(false);
            setPortalSlot(null);
            return;
        }

        const el = document.getElementById('apv-header-portal-slot');
        if (el) {
            setPortalSlot(el);
            setHasDocHeader(true);
        }

        return () => {
            setHasDocHeader(false);
        };
    }, [inlineHeader, setHasDocHeader]);

    // ── Audit Trail Tracking (Tự động nạp thông tin Người tạo & Người cập nhật) ──
    const isRealEntity = Boolean(
        entityId &&
        entityId !== 'new' &&
        entityId !== 'Đang tải...' &&
        entityType &&
        entityType !== 'general'
    );

    const { data: auditInfo, refetch: refetchAuditInfo } = useQuery({
        ...trpc.activityLog.getAuditInfo.queryOptions({
            entityType: entityType || 'general',
            entityId: entityId || '',
        }),
        enabled: isRealEntity,
        staleTime: 0,
    });

    // Sau mỗi thao tác ghi dữ liệu thành công (tRPC / REST) → nạp lại footer người tạo / cập nhật
    useEffect(() => {
        if (!isRealEntity) return;
        const onMutated = () => { void refetchAuditInfo(); };
        window.addEventListener(DATA_MUTATED_EVENT, onMutated);
        return () => window.removeEventListener(DATA_MUTATED_EVENT, onMutated);
    }, [isRealEntity, refetchAuditInfo]);

    // Nguồn chuẩn: getAuditInfo (cột của bản ghi + activity_logs); prop `timestamps` chỉ là dự phòng
    const effectiveCreatedAt = auditInfo?.createdAt || timestamps?.created;
    const effectiveCreatedBy = auditInfo?.createdByName || timestamps?.createdBy;
    const effectiveCreatedAvatar = auditInfo?.createdByAvatar;

    const effectiveUpdatedAt = auditInfo?.updatedAt || timestamps?.updated;
    const effectiveUpdatedBy = auditInfo?.updatedByName || timestamps?.updatedBy;
    const effectiveUpdatedAvatar = auditInfo?.updatedByAvatar;

    const handleOpenHistoryTab = useCallback(() => {
        window.dispatchEvent(new CustomEvent('apv:open-history-tab'));
    }, []);

    // Register title for current page into app navigation history
    const titleText = useMemo(() => {
        if (typeof title === 'string' && title.trim()) return title.trim();
        if (tabTitle && tabTitle.trim()) return tabTitle.trim();
        if (typeof subtitle === 'string' && subtitle.trim()) return subtitle.trim();
        if (entityId && entityId !== 'new') return entityId;
        return undefined;
    }, [title, tabTitle, subtitle, entityId]);

    useEffect(() => {
        if (titleText && titleText.trim()) {
            const prefix = entityType && ENTITY_PAGELIST_MAP[entityType]
                ? ENTITY_PAGELIST_MAP[entityType].label.replace('Danh sách ', '').trim() + ' '
                : '';
            const registeredTitle = prefix ? `${prefix}${titleText.trim()}` : titleText.trim();
            updateCurrentTitle(registeredTitle, entityType);
        }
    }, [titleText, entityType, updateCurrentTitle]);

    // Reset scroll position to top when navigating into detail page
    useEffect(() => {
        const el = document.querySelector('.apv-content-area');
        if (el) {
            el.scrollTop = 0;
            el.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [entityId]);

    // Calculate fallback target if internal session history stack is empty (e.g. direct link / new tab)
    const fallbackTarget = useMemo(() => {
        if (breadcrumbs && breadcrumbs.length > 0 && breadcrumbs[0].to) {
            return {
                to: breadcrumbs[0].to,
                params: breadcrumbs[0].params,
                label: breadcrumbs[0].label || 'Danh sách',
            };
        }
        if (entityType && ENTITY_PAGELIST_MAP[entityType]) {
            return {
                to: ENTITY_PAGELIST_MAP[entityType].to,
                label: ENTITY_PAGELIST_MAP[entityType].label,
            };
        }
        return {
            to: '/',
            label: 'Trang chủ',
        };
    }, [breadcrumbs, entityType]);

    // Back tooltip label
    const backTooltip = useMemo(() => {
        if (previousRoute?.title) {
            return `Quay lại: ${previousRoute.title}`;
        }
        if (previousRoute?.href) {
            return 'Quay lại trang trước';
        }
        return `Quay lại: ${fallbackTarget.label}`;
    }, [previousRoute, fallbackTarget]);

    const handleBackClick = useCallback(() => {
        // 1. If explicit override requested with custom onBack, use it
        if (overrideBack && onBack) {
            onBack();
            return;
        }

        // 2. If previous route exists in app internal session stack, navigate back to it!
        if (previousRoute && previousRoute.href) {
            navigate({ to: previousRoute.href as any });
            return;
        }

        // 3. Custom onBack callback (if passed and no previousRoute in session)
        if (onBack) {
            onBack();
            return;
        }

        // 4. Fallback to canonical entity pagelist
        if (fallbackTarget.to) {
            navigate({
                to: fallbackTarget.to as any,
                params: fallbackTarget.params as any,
            });
            return;
        }

        // 5. Fallback browser history
        router.history.back();
    }, [overrideBack, onBack, previousRoute, fallbackTarget, navigate, router]);

    // ── Build path title ──
    // Danh mục (link) luôn hiện và co trước; nút Quay lại chỉ là biểu tượng (tooltip ghi nơi quay về) —
    // trước đây nhãn nút Quay lại chiếm ~180px làm mã chứng từ bị ép mất khi trang có nhiều nút (Giao hàng, Kẽm CTP)
    const crumbLabel = breadcrumbs && breadcrumbs.length >= 1 ? breadcrumbs[breadcrumbs.length >= 2 ? breadcrumbs.length - 2 : 0].label : null;
    const pathTitle = breadcrumbs && breadcrumbs.length >= 1
        ? (
            <span className="flex items-center gap-1.5 whitespace-nowrap flex-nowrap min-w-0 overflow-hidden">
                {breadcrumbs.length >= 2 ? (
                    breadcrumbs[breadcrumbs.length - 2].to ? (
                        <Link
                            to={breadcrumbs[breadcrumbs.length - 2].to}
                            params={breadcrumbs[breadcrumbs.length - 2].params as any}
                            className="text-indigo-600 font-semibold hover:text-indigo-800 hover:underline transition-colors shrink min-w-0 truncate max-w-[120px] sm:max-w-[200px] @max-[30rem]:hidden"
                        >
                            {breadcrumbs[breadcrumbs.length - 2].label}
                        </Link>
                    ) : (
                        <span className="text-slate-500 font-medium shrink min-w-0 truncate max-w-[120px] sm:max-w-[200px] @max-[30rem]:hidden">{breadcrumbs[breadcrumbs.length - 2].label}</span>
                    )
                ) : (
                    breadcrumbs[0].to ? (
                        <Link
                            to={breadcrumbs[0].to}
                            params={breadcrumbs[0].params as any}
                            className="text-indigo-600 font-semibold hover:text-indigo-800 hover:underline transition-colors shrink min-w-0 truncate max-w-[120px] sm:max-w-[200px] @max-[30rem]:hidden"
                        >
                            {breadcrumbs[0].label}
                        </Link>
                    ) : (
                        <span className="text-slate-500 font-medium shrink min-w-0 truncate max-w-[120px] sm:max-w-[200px] @max-[30rem]:hidden">{breadcrumbs[0].label}</span>
                    )
                )}
                <span className="text-slate-300 font-normal shrink-0 @max-[30rem]:hidden">/</span>
                {/* Mã chứng từ co sau cùng: nhãn danh mục co trước, mã luôn đọc được khi nhiều nút thao tác */}
                <span className="text-slate-900 font-bold truncate shrink-0 max-w-[22rem]">{title}</span>
            </span>
        )
        : <span className="text-slate-900 font-bold truncate shrink-0 max-w-[22rem]">{title}</span>;

    const showBackButton = !hideBackButton && !isZIndex;

    const backLabel = useMemo(() => {
        // Nút Quay lại về trang trước trong phiên (nếu có) → ghi đúng tên trang đó; không có thì về danh mục
        if (previousRoute?.title) {
            return previousRoute.title;
        }
        if (breadcrumbs && breadcrumbs.length >= 1) {
            return breadcrumbs[breadcrumbs.length >= 2 ? breadcrumbs.length - 2 : 0].label;
        }
        if (fallbackTarget.label && fallbackTarget.label !== 'Quay lại' && fallbackTarget.label !== 'Trang chủ') {
            return fallbackTarget.label;
        }
        if (previousRoute?.title) {
            return previousRoute.title;
        }
        return 'Quay lại';
    }, [breadcrumbs, fallbackTarget, previousRoute]);

    const renderTimestamps = () => {
        if (!effectiveCreatedAt && !effectiveUpdatedAt) return null;
        return (
            <footer data-testid="detail-audit-footer" aria-label="Thông tin tạo và cập nhật" className="text-[11px] text-slate-500 pt-2 pb-2 flex items-center justify-end gap-3 flex-wrap">
                {effectiveCreatedAt && (
                    <div className="flex items-center gap-1.5" data-testid="audit-created">
                        <span>Tạo lúc <span data-testid="audit-created-at">{fmtTs(effectiveCreatedAt)}</span></span>
                        {effectiveCreatedBy && (
                            <>
                                <span className="text-slate-400">bởi</span>
                                <span data-testid="audit-created-by"><UserDisplay name={effectiveCreatedBy} avatar={effectiveCreatedAvatar} size="xs" textColor="text-slate-700" /></span>
                            </>
                        )}
                    </div>
                )}
                {effectiveCreatedAt && effectiveUpdatedAt && <span className="text-slate-300" aria-hidden="true">|</span>}
                {effectiveUpdatedAt && (
                    <div className="flex items-center gap-1.5" data-testid="audit-updated">
                        <span>Cập nhật lúc <span data-testid="audit-updated-at">{fmtTs(effectiveUpdatedAt)}</span></span>
                        {effectiveUpdatedBy && (
                            <>
                                <span className="text-slate-400">bởi</span>
                                <span data-testid="audit-updated-by"><UserDisplay name={effectiveUpdatedBy} avatar={effectiveUpdatedAvatar} size="xs" textColor="text-slate-700" /></span>
                            </>
                        )}
                    </div>
                )}
                {showContextPanel && entityId && (
                    <button type="button" onClick={handleOpenHistoryTab} className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline font-medium">
                        <History className="w-3 h-3" aria-hidden="true" />
                        Xem lịch sử
                    </button>
                )}
            </footer>
        );
    };

    const renderHeaderContent = (isModalMode: boolean) => (
        <div className={cn(
            "flex items-center justify-between gap-2 md:gap-3 w-full h-full min-w-0",
            isModalMode ? "py-0.5" : ""
        )} data-testid={isModalMode ? "modal-doc-header" : "top-header-doc-portal"}>
            {/* Left: Meta Info (Back Button + Path/Title + Status + Subtitle) — cắt phần thừa, không đè lên nút thao tác */}
            {/* @container: thiếu chỗ (trang nhiều nút) thì ẩn hẳn link danh mục, không để còn mảnh "G…" */}
            <div className="@container flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1 overflow-hidden">
                {showBackButton && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleBackClick}
                                className="-ml-1 shrink-0 h-7.5 px-2 gap-1 text-slate-700 hover:text-slate-900 hover:bg-slate-100 font-medium transition-colors"
                                aria-label={backTooltip}
                                data-testid="detail-back-button"
                            >
                                <ArrowLeft className="h-4 w-4 text-slate-600" />
                                {/* Màn rộng (≥1536px) mới ghi nhãn, và chỉ khi khác tên danh mục đã hiện bên cạnh */}
                                {backLabel !== crumbLabel && (
                                    <span className="hidden 2xl:inline text-xs font-semibold truncate max-w-[140px]">{backLabel ? backLabel.charAt(0).toUpperCase() + backLabel.slice(1) : backLabel}</span>
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start">
                            {backTooltip}
                        </TooltipContent>
                    </Tooltip>
                )}

                <span className="text-slate-300 font-normal shrink-0">/</span>

                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap sm:flex-nowrap">
                    {/* Mã chứng từ luôn hiện đủ: tiêu đề không co; thiếu chỗ thì nhãn trạng thái co / cắt bớt (đơn có 3 nhãn từng che mất mã) */}
                    <h2 className="text-xs sm:text-sm md:text-base font-bold text-slate-900 truncate tracking-tight shrink-0 max-w-[75%]">
                        {pathTitle}
                    </h2>

                    {/* Không hiển thị subtitle: thanh tiêu đề chỉ mã chứng từ + trạng thái (31/10/2026) */}

                    {status && (
                        <div className="flex items-center gap-1 min-w-0 shrink overflow-hidden scale-95 origin-left flex-nowrap [&>div]:flex-nowrap [&>div]:min-w-0 [&_*]:whitespace-nowrap" data-testid="detail-header-status">
                            {status}
                        </div>
                    )}
                </div>
            </div>

            {/* Right: Actions Bar */}
            {actions && (
                <div className={cn(
                    "items-center gap-1.5 shrink-0 ml-auto",
                    isModalMode ? "flex pr-16 md:pr-20" : "hidden md:flex"
                )}>
                    {showSync && entityId && entityId !== 'new' && (
                        <SyncStatusPill entityType={entityType} entityId={entityId} className="mr-1 scale-90" />
                    )}
                    <AdaptiveActionSplitter mode="desktop-only">
                        {actions}
                    </AdaptiveActionSplitter>
                </div>
            )}
        </div>
    );

    const isPortaled = Boolean(portalSlot && !inlineHeader);

    return (
        <div className={cn(
            "flex flex-col bg-slate-50/50 relative",
            isZIndex ? "min-h-0 flex-1" : underModuleTabs ? "min-h-full" : "min-h-screen"
        )}>
            {/* 1. Portal into Top Header when not in Modal/ZIndex mode */}
            {isPortaled && createPortal(
                renderHeaderContent(false),
                portalSlot!
            )}

            {/* 2. Fallback / Modal Header (Only shown when inside ZIndexView modal or before portal attaches) */}
            {!isPortaled && (
                <div role="region" aria-label="Thanh điều hướng tài liệu" className={cn(
                    "bg-white px-3 md:px-5 py-2 border-b border-slate-200/90 z-30 transition-all sticky top-0 shadow-xs"
                )}>
                    {renderHeaderContent(isZIndex || !underModuleTabs)}
                </div>
            )}

            {/* 3. Workflow Stepper Ribbon: Rendered as a slim, paper-thin ribbon at the very top of body */}
            {workflow && (
                <div role="region" aria-label="Tiến trình quy trình" className="bg-white border-b border-slate-200/80 px-3 md:px-5 py-2 shadow-2xs overflow-x-auto no-scrollbar">
                    {workflow}
                </div>
            )}

            {/* 4. Main Content */}
            <main className={cn(
                "flex-1 p-3 md:p-4 lg:p-5 pt-3 md:pt-4 animate-in fade-in duration-300 overflow-x-hidden flex flex-col gap-4",
                actions ? "pb-20 md:pb-4" : "pb-6 md:pb-4"
            )}>
                {children}

                {/* Context Panel: Bình luận, Đính kèm, Lịch sử */}
                {showContextPanel && entityId && entityId !== "Đang tải..." && (
                    <EntityContextPanel entityType={entityType} entityId={entityId} />
                )}

                {/* Footer Tạo lúc … bởi … | Cập nhật lúc … bởi … (bị gỡ nhầm ở 13ecff106, khôi phục) */}
                {renderTimestamps()}
            </main>

            {/* 5. Mobile Sticky Bottom Action Bar (Thumb-friendly, perfect mobile ergonomics) */}
            {actions && (
                <div className={cn(
                    "md:hidden bg-white/95 backdrop-blur-md border-t border-slate-200/90 px-3 py-2 flex items-center justify-between gap-2 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] z-40",
                    isZIndex ? "sticky bottom-0 mt-auto" : "fixed bottom-0 left-0 right-0 pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
                )}>
                    {showSync && entityId && entityId !== 'new' && (
                        <div className="shrink-0 mr-1">
                            <SyncStatusPill entityType={entityType} entityId={entityId} className="scale-90" />
                        </div>
                    )}
                    {/* Tối đa 4 nút trên một hàng, nút kém quan trọng vào "…" (chuẩn như trang danh sách) */}
                    <div ref={mobileOverflow.ref} className="erp-detail-actions flex items-center gap-1.5 py-0.5 w-full justify-end pr-10">
                        {actions}
                        <MobileOverflowMenu items={mobileOverflow.hidden} onSelect={mobileOverflow.activate} />
                    </div>
                </div>
            )}
        </div>
    );
};

export { StandardDetailLayout };
export default StandardDetailLayout;
